import debounce from 'debounce';
import { type DocumentReference } from 'firebase/firestore';

import { getAppBroker } from 'stores/AppBroker';
import * as DatabasePaths from 'stores/DatabasePaths';
import type Template from 'stores/Template';
import { AdEvents } from 'types/AdEvents';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentType } from 'types/DocumentType';
import { DynamicPropertyEvent } from 'types/DynamicPropertyEvent';
import { serverTimestamp } from 'utils/FirestoreUtils';

import type AdsManager from './AdsManager';
import CommentsManager from './CommentsManager';
import type Creative from './Creative';
import { DocumentSchema } from './DocumentSchema';
import DocumentStore from './DocumentStore';
import { ifSVGText } from './DynamicProperties/casts';
import {
    areDynamicPropertyDataArraysEqual,
    cloneDynamicPropertyData,
    cloneDynamicPropertyDataArray,
    type DynamicPropertyData,
} from './DynamicProperties/data';
import DynamicPropertiesManager from './DynamicProperties/Manager';

export class AdSchema extends DocumentSchema {
    dynamicData: DynamicPropertyData[] = [];
    index = NaN;
    name = '';
    label = '';
    type = DocumentType.AD;
    numComments: number = 0;
    viewHistory: Record<string, { seconds: number }> = {};
    dateModified = serverTimestamp();
}

export function newAdSchemaBasedOn({
    dynamicData,
    index,
    name,
    label,
    type
}: AdSchema): AdSchema {
    const newObj = new AdSchema();
    newObj.dynamicData = dynamicData.map(d => cloneDynamicPropertyData(d));
    newObj.index = index;
    newObj.name = name;
    newObj.label = label;
    newObj.type = type;
    return newObj;
}

export default class Ad extends DocumentStore<AdSchema> {
    declare parent: AdsManager | undefined;

    schema = new AdSchema();
    type = DocumentType.AD;
    collectionType = DocumentCollectionType.AD;
    active = false;
    isRead = true;
    selected = false;
    comments: CommentsManager = new CommentsManager(this);

    properties = new DynamicPropertiesManager(this, 'Ad');

    get numComments() {
        return this.data.numComments;
    }

    constructor(parent: AdsManager, docRef?: DocumentReference) {
        super(parent, docRef);
        this.comments.enable();
        // Cancel any in-flight debounce when this store is reset/disposed, so
        // a pending call can't fire into a torn-down object. `clear` closes
        // over its own timer, so it is safe to pass unbound.
        this.disposer.add(this.onPropertyModified.clear);
        this.becomeObservable({ except: ['properties', 'comments'] });
    }

    getTemplateId(): string | undefined {
        const templateProperty = this.data.dynamicData.find(
            (i) => i.name === 'template'
        );
        if (templateProperty) {
            const { value } = templateProperty;
            if (typeof value === 'string') return value;
        }
        return undefined;
    }

    getTemplate(): Template | undefined {
        const templateId = this.getTemplateId();
        if (templateId != null) {
            const parentCreative = this.parentCreative;
            if (parentCreative) {
                return parentCreative.templates.getById(templateId);
            }
        }
        return undefined;
    }

    navigateTo = () => {
        const pathParams = DatabasePaths.ad.requireMatch(this.docRef.path);
        void getAppBroker().navigate(pathParams, { keepQueryString: true });
    }

    // Declared since 2021 and, until `template` above started using it, never
    // called from anywhere - while five other files reached for the globally
    // current creative instead.
    //
    // Undefined once this ad has been reset: DocumentStore.reset() assigns
    // `this.parent = undefined`, and reset does not always coincide with the ad
    // leaving its collection. DocumentCollection.remove() resets the child and
    // only then awaits the delete, so `ads.current` keeps returning a reset ad
    // across that await; onRemoved() splices and then resets, within one
    // synchronous pass. Since `parent` is observable - it is assigned in the
    // DocumentStore constructor, ahead of becomeObservable() - the reset itself
    // invalidates anything computed from it, so a reader can be woken precisely
    // when the answer is undefined. `root.creative` never had this exposure,
    // which is the one thing it had going for it.
    get parentCreative(): Creative | undefined {
        return this.parent?.parent;
    }

    // get changed() { // TODO: will likely need to rewire the UI to the dynamic properties manager.
    //     return this.properties.changed;
    // }

    get label() {
        return this.data.dynamicData.find( i => i.name === 'label')?.value || '';
    }

    cloneData = () => {
        return newAdSchemaBasedOn(this.data);
    }

    onPropertyModified = debounce(() => {
        this.dispatch(AdEvents.CHANGED);
    }, 10);

    onUpdated = (data: AdSchema, oldData: AdSchema) => {
        // if(this.active === true) return;
        const dynamicPropertiesChanged = !areDynamicPropertyDataArraysEqual(
            data.dynamicData,
            oldData?.dynamicData
        );
        if(dynamicPropertiesChanged) {
            this.properties.setState(data.dynamicData);
            this.properties.resetChanged();
        }
    }
    
    onReady = async () => {
        if (!this.active) {
            // this probably *shouldn't* need to be cloned; i'm presuming
            // setState mutates the input value somehow. this seems to be
            // a consistent pattern
            this.properties.setState(
                cloneDynamicPropertyDataArray(this.data.dynamicData)
            );
        }
    }

    onBeforeSave = async () => {
        // this.saveDynamicProperties(false);
    }

    async saveDynamicProperties(syncToDB: boolean) {
        this.data.dynamicData = this.properties.getData();
        this.properties.resetChanged();
        if (syncToDB) await this.save();
    }

    selectToggle() {
        this.selected = !this.selected;
    }

    // View history belongs to the comments feature; this lets the shared
    // Comments UI (via its Commentable interface) record a view on an Ad.
    updateViewHistory() {
        return this.comments.updateViewHistory();
    }

    setSelected(selected: boolean) {
        this.selected = selected;
    }

    // Local only - this does not persist. Index changes are written in one
    // batch afterwards by AdsManager.updateIndexes(), which calls the
    // updateAdIndexes cloud function.
    //
    // A `setIndexLocal` twin existed for exactly that distinction, back when
    // this method ended with `this.saveData()`. That call was dropped in
    // 2d59afb4 (2022-08-15) when per-ad writes were causing multiplayer
    // conflicts during CSV import, which left the two identical.
    setIndex(index: number) {
        this.data.index = index;
    }

    activate = async () => {
        // this.properties.setState(this.data.dynamicData);
        this.properties.addEventListener(DynamicPropertyEvent.CHANGED, this.onPropertyModified);
        this.active = true;
        if (!this.comments.loaded) await this.comments.load();
    }

    renderAllSVGProperties = async () => {
        // consider: Promise.all? That would parallelize the font
        // loading. Font parsing and text rendering would still be
        // serialized in the main thread
        for (const prop of this.properties.properties) {
            const svgProp = ifSVGText(prop);
            if (svgProp) {
                await svgProp.renderTextPaths(true);
            }
        }
    };

    deactivate = () => {
        if (!this.active) return;
        this.active = false;
        // this.properties.reset();
        this.properties.removeEventListener(DynamicPropertyEvent.CHANGED, this.onPropertyModified);
    }

    reload = () => {
        console.log('ad reload');
        this.dispatch(AdEvents.RELOAD);
    }
}
